import React, { useState } from "react";
import { Link } from "react-router-dom";
import API from "../../utils/API";
import { Badge, Button, useToast } from "../../ui";
import { formatDate } from "../../data/format";
import PolicyStatusAction from "../clients/PolicyStatusAction";
import LinkClientAction from "../pipeline/LinkClientAction";
import DismissMenu from "./DismissMenu";
import ReceiptRow from "./ReceiptRow";
import {
  actionKindFor,
  evidenceRows,
  groupForRule,
  proposalFor,
  subjectProspect,
  suspectPolicy
} from "./findingGrammar";

// Reactivate is the reversal for a policy inactivate — the same routes
// PolicyStatusAction uses; a mis-clicked flip is undone by flipping it back.
const REACTIVATE = {
  auto: (id) => API.reactivateAuto(id),
  home: (id) => API.reactivateProperty(id),
  umbrella: (id) => API.reactivateUmbrella(id)
};

/**
 * One Data-Fix finding card (§B3). Propose-don't-push: the server-authored
 * sentence states the fact, the proposal hedges, and the primary button names
 * the exact change — driving J's existing callable affordances
 * (PolicyStatusAction / LinkClientAction / quote-update) or deep-linking to the
 * record's drawer. An unclear finding flips to "Ask the Fawn"; dismiss asks why.
 *
 * Every action is reversible (§B0 law 3 / §B4) via a DELAYED-COMMIT that lives on
 * the PAGE (DataFixesPage): the apply fires immediately (a policy flip / quote
 * update, reversible by its own reversal), then this card hands the page a
 * pending resolve via onBeginResolve; the page holds the 10s undo window and the
 * PATCH resolve, so a chip switch that unmounts this card never commits early or
 * loses the receipt. The card renders its receipt from the `resolution` prop and
 * calls onUndoResolve while the window is open; the instant it commits, the undo
 * affordance dies (no post-window half-undo).
 */
function FindingCard({ finding, resolution, onBeginResolve, onUndoResolve, onAsked }) {
  const toast = useToast();
  const [dismissing, setDismissing] = useState(false);
  const [applying, setApplying] = useState(false); // quote-link apply spinner

  const rule = (finding.meta && finding.meta.rule) || finding.rule;
  const group = groupForRule(rule);
  const kind = actionKindFor(rule);
  const proposal = proposalFor(rule);
  const rows = evidenceRows(finding);
  const policy = kind === "policy" ? suspectPolicy(finding) : null;
  const prospect = kind === "link" ? subjectProspect(finding) : null;
  // quote_prospect_link: the matched prospect to attribute the quote to.
  const quoteCandidate = kind === "quote-link" ? subjectProspect(finding) : null;
  // Confident only with a concrete single target; otherwise flip to Ask the Fawn.
  const ambiguous =
    (kind === "policy" && !policy) ||
    (kind === "link" && !prospect) ||
    (kind === "quote-link" && !quoteCandidate);
  const clientHref = finding.clientRef ? `/app/clients/${finding.clientRef}` : null;
  const deepLink = kind === "record" || kind === "review";
  const foundWhen = formatDate(finding.created_at); // "—" when absent/unparseable (E L-2)

  const onApplied = () => {
    const reversal =
      kind === "policy" && policy && REACTIVATE[policy.lobKey]
        ? () => REACTIVATE[policy.lobKey](policy._id)
        : null;
    onBeginResolve(finding, {
      label: `Resolved — ${finding.body}`,
      resolution: "resolved",
      reversal
    });
  };

  // quote_prospect_link (Coordinator-confirmed ATTRIBUTE-QUOTE): the apply is a
  // quote update carrying {prospectRef} through the existing endpoint (which
  // format-validates it, existence-checks the prospect, and auto-defaults the
  // referrer attribution server-side) — NOT LinkClientAction. Then delayed-commit
  // resolve, reversal = unset the attribution.
  const applyQuoteLink = async () => {
    if (!quoteCandidate) {
      return;
    }
    setApplying(true);
    try {
      await API.updateQuote(finding.subjectRef, { prospectRef: quoteCandidate._id });
      onBeginResolve(finding, {
        label: `Linked the quote to ${quoteCandidate.name}`,
        resolution: "resolved",
        reversal: () => API.updateQuote(finding.subjectRef, { prospectRef: null })
      });
    } catch (err) {
      toast.error("Couldn't link the quote — try again.");
    } finally {
      setApplying(false);
    }
  };

  const markResolved = () =>
    onBeginResolve(finding, { label: `Resolved — ${finding.body}`, resolution: "resolved" });

  const dismiss = (note) => {
    setDismissing(false);
    onBeginResolve(finding, {
      label: `Dismissed — ${finding.body}`,
      dismissed: true,
      resolution: "dismissed",
      note
    });
  };

  const ask = async () => {
    const askBody = `Custodian flagged: ${finding.body} — which one is current?`;
    try {
      const res = await API.createQuestion({
        subjectModel: finding.subjectModel,
        subjectRef: finding.subjectRef,
        body: askBody
      });
      toast.success("Asked the Fawn — it'll come back with what it finds.");
      // L-4: optimistically surface the new question so the Questions chip bumps
      // without a reload (server shape: 201 {ok, id, thanks}).
      if (onAsked && res && res.data && res.data.id) {
        onAsked({
          _id: res.data.id,
          body: askBody,
          status: "open",
          subjectModel: finding.subjectModel,
          subjectRef: finding.subjectRef,
          created_at: new Date().toISOString()
        });
      }
      // Asking creates a question (a parallel track); it does NOT resolve the
      // finding — a display-only receipt (no undo, no PATCH), page-owned.
      onBeginResolve(finding, { label: `Asked the Fawn — ${finding.body}`, resolution: null });
    } catch (err) {
      toast.error("Couldn't reach the Fawn — try again.");
    }
  };

  if (resolution) {
    // The receipt is page-owned so it survives chip-switch remounts. Undo lives
    // ONLY while the resolve is pending (E H-1): the instant it commits, the
    // affordance dies — a post-commit reactivate would recreate the problem with
    // no finding left to catch it.
    return (
      <ReceiptRow
        label={resolution.label}
        dismissed={resolution.dismissed}
        onUndo={resolution.phase === "pending" ? () => onUndoResolve(finding._id) : undefined}
      />
    );
  }

  return (
    <div className="fk-card feat-finding">
      <div className="fk-card__body">
        <div className="feat-finding__top">
          <Badge variant="neutral">{group}</Badge>
          {foundWhen !== "—" ? (
            <span className="feat-finding__found">found {foundWhen}</span>
          ) : null}
        </div>

        <div className="feat-finding__sentence">{finding.body}</div>

        {rows.length > 0 ? (
          <div className="feat-evidence">
            {rows.map((r) => (
              <div
                key={r.key}
                className={`feat-evidence__row${r.suspect ? " feat-evidence__row--suspect" : ""}`}
              >
                <span>{r.label}</span>
                {r.suspect ? <span aria-hidden="true"> ⚠</span> : null}
                {r.why ? <span className="feat-evidence__why">{r.why}</span> : null}
              </div>
            ))}
          </div>
        ) : null}

        <div className="feat-finding__proposal">
          {ambiguous ? (
            `The Custodian isn't sure — ${proposal.text}.`
          ) : (
            <>
              Custodian suggests: <b>{proposal.actionLabel || "a review"}</b> ({proposal.text}).
            </>
          )}
        </div>

        <div className="feat-finding__actions">
          {ambiguous ? (
            <Button variant="primary" onClick={ask}>
              🦌 Ask the Fawn
            </Button>
          ) : kind === "policy" ? (
            <PolicyStatusAction
              policy={policy}
              inactiveLabel={policy.label}
              onChanged={onApplied}
              variant="primary"
            />
          ) : kind === "link" ? (
            <LinkClientAction prospect={prospect} onLinked={onApplied} variant="primary" />
          ) : kind === "quote-link" ? (
            <Button variant="primary" loading={applying} onClick={applyQuoteLink}>
              Link quote to {quoteCandidate.name}
            </Button>
          ) : deepLink && clientHref ? (
            <Link to={clientHref}>
              <Button variant="primary">Open the record →</Button>
            </Link>
          ) : null}

          {clientHref && !deepLink ? (
            <Link to={clientHref}>
              <Button variant="secondary">Open household</Button>
            </Link>
          ) : null}

          {deepLink ? (
            <Button variant="secondary" onClick={markResolved}>
              Mark resolved
            </Button>
          ) : null}

          <span className="feat-finding__dismiss">
            <Button variant="ghost" onClick={() => setDismissing((v) => !v)}>
              Dismiss…
            </Button>
            {dismissing ? (
              <DismissMenu onDismiss={dismiss} onClose={() => setDismissing(false)} />
            ) : null}
          </span>
        </div>
      </div>
    </div>
  );
}

export default FindingCard;
